<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                :title="pageTitle"
                @onClickCloseButton="goToList"
            />
        </div>

        <div class="my-2 px-2">
            <TableHeaderIPDBillAdd
                :formData="formData"
                :patient="patient"
                :tableItems="tableItems"
                :referrer="referrer"
            />
        </div>

        <div class="col-12 px-2">
            <ListTableUpdate
                :tableItems="tableItems"
                @addNewItem="addNewItem"
                :isHideAddBtn="true"
                :vatRate="vatRate"
                :salesAccountHeads="salesAccountHeads"
                :discountAccountHeads="discountAccountHeads"
                :isUpdate="true"
                :isMoneyReceipt="false"
            />
        </div>
        <div class="row px-2">
            <div class="col-md-6">
                <div class="mb-1">
                    <label class="form-label" for="description">Ledger Memo</label>
                    <vField
                        as="textarea"
                        name="description"
                        type="number"
                        class="form-control"
                        v-model="formData.description"
                    />
                </div>
            </div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button :disabled="loading" @click="handleSubmit(false)" class="btn btn-primary">Update</button>
                </div>
            </div>
        </div>
        
    </div>
</template>

<script setup>
import {inject, onMounted, provide, ref, computed} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import ListTableUpdate from '@/components/molecule/company/hospital/billing/ipd-bill/InvoiceIPDTableUpdate.vue';
import {useRoute, useRouter} from 'vue-router';
import {generateUUID} from '@/services/utils/global';
import handlePurchase from '@/services/modules/purchase';
import {vatRate} from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';
import TableHeaderIPDBillAdd from "@/components/molecule/company/hospital/billing/ipd-bill/TableHeaderHospitalBillAdd.vue";
import handleHospital from "@/services/modules/hospital";

const { generatePdf } = hospitalPdfPrinterHelper();
    const { fetchProductList, fetchSalesBill, ...rest } = handlePurchase();
    const { fetchCompanyInfo } = handleCompany();
    const {fetchSingleNote} = handleHospital();

    const $route = useRoute();
    const $router = useRouter();

    const companyId = $route.params.companyId;
    const loader = ref(true);
    const loading = ref(false);
    const salesAccountHeads = ref([]);
    const totals = ref({});
    const invoiceRes = ref({});
    const company = ref({});
    const patient = ref({});
    const referrer = ref(null);
    const accountPayableHeadId = ref(null);
    const discountAccountHeads = ref([]);

    const isCard = computed(() => {
        return $route.query.isCard
    })

    const pageTitle = computed(() => {
        if (!$route.query.bill_title) return 'Update IPD Bill';
        return $route.query.bill_title;
    })

    let formData = ref({
        company_id: companyId,
        contact_profile_id: null,
        account_head_id: null,
        ipd_register_id: null,
        vat_payable_account_head_id: null,
        mop_account_head_id: null,
        receipt_reference: null,
        receipt_description: "IPD bill payment",
        payment: null,
        project_id: null,
        cost_centre_id: null,
        location_id: null,
        discount_head_id: null,
        adjustment_amount: 0,
        service_charge_head_id: null,
        service_charge_amount: 0,
        status: 'active',
        bill_number: '',
        money_receipt_no: '',
        date: '',
        has_item_detail: true,
        description: '',
        order_number: null,
        sales_person_id: null,
        commission_percent: null,
        lc_number: null,
        item_details: []
    });
    let tableItems = ref([]);

    provide('formData', formData);
    provide('totals', totals);
    const showSuccess = inject('showSuccess');
    const showError = inject('showError');

    onMounted( async() => {
        const companyQuery = `?company_id=${companyId}`;
        const receiveAbleRes = rest.fetchAccountReceivable(companyQuery);
        const payableRes = rest.fetchAccountPayable(companyQuery);
        const vatPayableRes = rest.fetchVatPayable(companyQuery);
        const fetchCompany = fetchCompanyInfo(companyId);
        const id = $route.params.ipdId;
        const salesAccountHeadsRes = rest.getAccountHeadBySlag("sales_accounts", companyQuery);
        const discountAccountHeadsRes = rest.getAccountHeadBySlag("discount_account", companyQuery)

        Promise.all([
            receiveAbleRes.then(res=> {
                if(res.data) formData.value.account_head_id = res.data[0].id;
            }),
            vatPayableRes.then(res=> {
                if(res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
            }),
            fetchCompany.then(res=> {
                if(res.data) company.value = res.data;
            }),
            salesAccountHeadsRes.then(res => {
                if(res.data) salesAccountHeads.value = res.data
            }),
            discountAccountHeadsRes.then(res => {
                if(res.data) discountAccountHeads.value = res.data
            }),
            fetchSingleNote(companyQuery, $route.params.ipdRegisterId).then(res => {
                if (res.data) {
                    patient.value = res.data;
                    formData.value.contact_profile_id = res.data.patient.id
                    formData.value.ipd_register_id = res.data.id
                    formData.value.service_resource_id = res.data.human_resource_id
                }
            }),
            fetchSalesBill(id, companyQuery).then( (res) =>{
              if( res ){
                  const { receipt_generals } = res.data
                  formData.value = {
                      company_id: res.data.company_id,
                      contact_profile_id: res.data.contact_profile_id,
                      account_head_id: res.data.account_head_id,
                      ipd_register_id: res.data.ipd_register_id,
                      referrer_id: res.data.referrer_id,
                      vat_payable_account_head_id: res.data.vat_payable_account_head_id,
                      receipt_description: "IPD bill payment",
                      project_id: res.data.project_id,
                      cost_centre_id: res.data.cost_centre_id,
                      location_id: res.data.location_id,
                      discount_head_id: res.data.discount_head_id,
                      adjustment_amount: res.data.round_up_discount,
                      status: 'active',
                      bill_number: res.data.bill_number,
                      date: res.data.date,
                      has_item_detail: res.data.has_item_detail,
                      description: res.data.description,
                      order_number: res.data.order_number,
                      sales_person_id: res.data.sales_person_id,
                      commission_percent: res.data.commission_percent,
                      lc_number: res.data.lc_number,
                      service_charge_amount: res.data.service_charge_amount,
                      item_details: res.data.general,
                      service_charge_head_id: res.data.service_charge_head_id,
                  };
                  tableItems.value = res.data.general.map(({product, ...item}) => {
                      return {
                          ...item,
                          name: product.name,
                          product: {
                              name: product.name
                          },
                      }
                  })

                  if (receipt_generals && receipt_generals[0]) {
                      formData.value.money_receipt_no = receipt_generals[0].receipt_master.voucher_no
                      formData.value.mop_account_head_id = receipt_generals[0].receipt_master.account_head_id
                      formData.value.receipt_master_id = receipt_generals[0].receipt_master.id;
                      formData.value.receipt_reference = receipt_generals[0].receipt_master.receipt_reference
                      formData.value.payment = receipt_generals[0].receipt_master.total_paid_amount
                  }

                  if(res.data.referrer_id){
                      referrer.value = {
                        id: res.data.referrer_id,
                        name: res.data.referrer_name
                      }
                  }
              }
            }).catch( (err) => {
              console.log(err);
            }),
            payableRes.then(res=> {
              if(res.data){
                formData.value.account_payable_head_id = res.data[0].id;
                accountPayableHeadId.value = res.data[0].id;
              }
            }),
        ])
        .then(() => {loader.value=false})
        .catch(()=>{loader.value=false})
    })
    const addNewItem = () => {

        tableItems.value.push({
            id: null,
            product_id: '',
            name: '',
            description: '',
            quantity : 1,
            rate: 0,
            discount_percent: 0,
            vat: 15,
            sub_total: 0,
            discount_amount: 0,
            vat_amount: 0,
            total: 0,
            is_edit: false,
        });

    }

    const goToList = () => {
        const backUrl = $route.query.back_url ? decodeURIComponent($route.query.back_url) : null;
        if (backUrl) {
            $router.push(backUrl);
            return;
        }
        isCard.value ? $router.back() :
        $router.push({name: 'ipd-bill-list',
          params: $route.params,
          query: $route.query
        })
    }

    const handleSubmit = async (print=false) => {
        if(!formData.value.date) formData.value.date = new Date().toISOString().split('T')[0];
        formData.value.item_details = tableItems;
        let copyFormData = JSON.parse(JSON.stringify(formData.value))
        copyFormData.item_details = formData.value.item_details
        copyFormData.sale_type = "ipd_invoice"
        copyFormData.receipt_type = "ipd_invoice"
        copyFormData.account_payable_head_id = accountPayableHeadId.value

        loading.value = true;

        await rest.updateSalesBill($route.params.ipdId, copyFormData)
        .then(res => {  
            if(res.status) {
                showSuccess(res.message)
                if(print) goToPrint(res.data.id)
                goToList();
                return;
            }
            showError(res.message)
        })
        .catch(err=> {
            showError(err.response.data.message)
        })
        .finally(res => {
            loading.value = false;
        });
    }

    const goToPrint = (id) => {
        const companyQuery = `?company_id=${companyId}`;

        fetchSalesBill(id, companyQuery).then( (res) =>{
            if( res ){
                invoiceRes.value = res.data;
            }
        }).then( () => {
            generatePdf(company.value, invoiceRes.value, 'BILL', true, true)
        }).catch( (err) => {
            console.log(err);
        })
    }

</script>
<style scoped>
address{
    margin-bottom: 0 !important;
}
</style>